import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AppraisalFeeInfo,
  CreditCardPaymentRequest,
  CreditCardPaymentResponse,
  CreditConfigurationInfo,
  ENVIRONMENT,
  LoanLockResponse,
  NonPciCreditCardPaymentRequest,
  ZipLookup,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class OriginationCreditCardService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async submitPayment(
    loanId: string,
    borrowerId: string,
    request: CreditCardPaymentRequest,
  ): Promise<CreditCardPaymentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/pcicreditcard/charge`;

    try {
      return await firstValueFrom(this.http.post<CreditCardPaymentResponse>(url, request));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error submitting credit card payment for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Payment failed: ${message}`);
    }
  }

  async submitNonPciPayment(
    loanId: string,
    borrowerId: string,
    request: NonPciCreditCardPaymentRequest,
  ): Promise<CreditCardPaymentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/creditcard/charge`;

    try {
      return await firstValueFrom(this.http.post<CreditCardPaymentResponse>(url, request));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error submitting non-PCI credit card payment for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Payment failed: ${message}`);
    }
  }

  async getAppraisalFee(loanId: string, borrowerId: string): Promise<AppraisalFeeInfo> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/appraisal`;

    try {
      return await firstValueFrom(this.http.get<AppraisalFeeInfo>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting appraisal fee for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to get appraisal fee: ${message}`);
    }
  }

  async lookupZip(zip: string): Promise<{ city: string; state: string } | null> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/geo/zip/${zip}`;

    try {
      const results = await firstValueFrom(this.http.get<Array<{ city: string; stateCode: string }>>(url));
      if (results && results.length > 0) {
        return { city: results[0].city, state: results[0].stateCode };
      }
      return null;
    } catch {
      return null;
    }
  }

  async getPaymentLockStatus(loanId: string): Promise<LoanLockResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/paymentlockstatus`;

    try {
      return await firstValueFrom(this.http.get<LoanLockResponse>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting payment lock status for loan ${loanId}`);
      throw new Error(`Failed to get payment lock status: ${message}`);
    }
  }

  async getCreditCardConfigurations(loanId: string): Promise<CreditConfigurationInfo> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/creditcardconfigurations`;

    try {
      return await firstValueFrom(this.http.get<CreditConfigurationInfo>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error getting credit card configurations for loan ${loanId}`);
      throw new Error(`Failed to get credit card configurations: ${message}`);
    }
  }

  async searchZipCode(zipCode: string): Promise<ZipLookup[]> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/service/zipcode?zipCode=${zipCode}`;

    try {
      return await firstValueFrom(this.http.get<ZipLookup[]>(url));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.loggingService.logException(error, `Error searching zip code ${zipCode}`);
      throw new Error(`Failed to search zip code: ${message}`);
    }
  }

  async initializePayment(
    loanId: string,
    zipCode?: string,
  ): Promise<{
    lockStatus: LoanLockResponse;
    creditConfig: CreditConfigurationInfo;
    zipInfo?: ZipLookup[];
  }> {
    const [lockStatus, creditConfig] = await Promise.all([
      this.getPaymentLockStatus(loanId),
      this.getCreditCardConfigurations(loanId),
    ]);

    if (lockStatus.locked && !lockStatus.paymentLightsOut) {
      throw new Error(
        'Your Loan Officer is working on your file. Please contact your Loan Officer to process payment.',
      );
    }

    let zipInfo: ZipLookup[] | undefined;
    if (zipCode) {
      try {
        zipInfo = await this.searchZipCode(zipCode);
      } catch {
        // Zip lookup failure is not critical - continue without it
      }
    }

    return { lockStatus, creditConfig, zipInfo };
  }
}
